import { z } from 'zod';
import { specRowSchema } from './catalog.js';

/**
 * The Avrahami catalog feed, version 1: what `avrahami-backend` serves at
 * `/api/catalog-feed/v1` and the platform ingests (docs/CATALOG_FEED.md). The backend builds each
 * record from an explicit field whitelist; the platform validates every record against these
 * schemas and skips a record that does not match. Unknown fields are stripped.
 *
 * Never in the feed: cost or purchase prices, any price list but the agreed end-customer list,
 * stock and warehouse data, suppliers, customers, orders or documents. The one exception is the
 * customer a store is (`/customers/<card code>`, adr/0038): its name, its state and the prices it
 * gets in the original store, asked for one customer at a time.
 */
export const CATALOG_FEED_VERSION = 'v1';

/** Largest page the feed serves. */
export const CATALOG_FEED_MAX_PAGE = 500;

const text = (max: number) => z.string().trim().min(1).max(max);

export const feedCategorySchema = z.object({
  /** Stable key at the source: `m:<code>` for a main category, `m:<code>/g:<group>` below it. */
  key: text(200),
  name: text(120),
  parentKey: text(200).nullable(),
  position: z.number().int().min(0).max(100_000),
  /** Hidden on Avrahami's own store; a tenant copy starts hidden too. */
  hidden: z.boolean(),
  /**
   * The picture Avrahami's own store shows for the category (adr/0049): an absolute https image
   * URL, or null. Missing in a record built before the field existed.
   */
  image: z
    .url({ protocol: /^https$/ })
    .max(2_000)
    .nullish()
    .transform((value) => value ?? null),
  status: z.enum(['ACTIVE', 'DISCONTINUED']),
  changedAt: z.iso.datetime(),
});
export type FeedCategory = z.infer<typeof feedCategorySchema>;

export const feedPriceSchema = z.object({
  amountMinor: z.number().int().min(0).max(1_000_000_000),
  currency: z.literal('ILS'),
  includesVat: z.boolean(),
});
export type FeedPrice = z.infer<typeof feedPriceSchema>;

export const feedProductSchema = z.object({
  itemCode: text(50),
  status: z.enum(['ACTIVE', 'DISCONTINUED']),
  name: text(200),
  barcode: text(64).nullable(),
  model: text(100).nullable(),
  manufacturer: text(100).nullable(),
  /**
   * The model, the manufacturer and the barcode, which Avrahami's own store does not show as
   * fields: a model or a manufacturer appears only where a product's page writes it (and not at all
   * where the staff hid it), a barcode not at all. The fields above are null in the current feed. A store gets a value from here only when the
   * platform staff expose that field to it (adr/0030). Missing in a record stored before the
   * field existed.
   */
  withheld: z
    .object({
      model: text(100).nullable(),
      manufacturer: text(100).nullable(),
      /** Added after the first two: missing in a record built before it. */
      barcode: text(64)
        .nullish()
        .transform((value) => value ?? null),
    })
    .nullish()
    .transform((value) => value ?? { model: null, manufacturer: null, barcode: null }),
  /** Keys of the categories the item is placed in (at most two at the source). */
  categoryKeys: z.array(text(200)).max(10),
  /** Sold as a set of other items at the source (a bill of materials). */
  isBundle: z.boolean(),
  /** The agreed end-customer list price; null when the item has none. */
  price: feedPriceSchema.nullable(),
  specs: z.array(specRowSchema).max(100),
  /** Avrahami's marketing text: input for AI rewriting only, never shown on a tenant store. */
  marketing: z.object({
    title: text(300).nullable(),
    description: text(20_000).nullable(),
    highlights: z.array(text(1_000)).max(50),
    /**
     * The original site's "ideal for" lines and its care and cleaning instructions (adr/0054):
     * rewritten for each store with its copy. Missing in a record built before they were sent.
     */
    idealFor: z.array(text(300)).max(20).default([]),
    care: z.array(text(300)).max(20).default([]),
  }),
  /** Absolute https image URLs, in display order. */
  images: z.array(z.url({ protocol: /^https$/ }).max(2_000)).max(20),
  /**
   * The manufacturer logo Avrahami's own store shows on the product (adr/0041): an absolute
   * https image URL, or null. Missing in a record built before the field existed.
   */
  manufacturerLogo: z
    .url({ protocol: /^https$/ })
    .max(2_000)
    .nullish()
    .transform((value) => value ?? null),
  /** When the snapshot record last changed. Pages are ordered by it. */
  changedAt: z.iso.datetime(),
});
export type FeedProduct = z.infer<typeof feedProductSchema>;

/** A page of products. Items are validated one by one (see the ingest), so they arrive raw. */
export const feedProductPageSchema = z.object({
  version: z.literal(CATALOG_FEED_VERSION),
  generatedAt: z.iso.datetime().nullable(),
  items: z.array(z.unknown()).max(CATALOG_FEED_MAX_PAGE),
  nextCursor: z.string().max(512).nullable(),
});
export type FeedProductPage = z.infer<typeof feedProductPageSchema>;

export const feedCategoryListSchema = z.object({
  version: z.literal(CATALOG_FEED_VERSION),
  generatedAt: z.iso.datetime().nullable(),
  items: z.array(z.unknown()).max(10_000),
});
export type FeedCategoryList = z.infer<typeof feedCategoryListSchema>;

/** A customer's card code in Avrahami's books (SAP): the store's customer number. */
export const CUSTOMER_NUMBER_PATTERN = /^[A-Za-z0-9._-]{1,15}$/;

/**
 * One customer of Avrahami's original store and the prices it gets there (adr/0038): its price
 * list's price, or a special price of its own that is valid today. Only the items it has a price
 * for (a discontinued one at its last price): the original store hides the others from it. `priceList` is null when the customer has no
 * price list the platform may read (none of its own, or the cost list); it then has no prices.
 */
export const feedCustomerSchema = z.object({
  version: z.literal(CATALOG_FEED_VERSION),
  generatedAt: z.iso.datetime().nullable(),
  customer: z.object({
    cardCode: z.string().regex(CUSTOMER_NUMBER_PATTERN),
    name: text(100).nullable(),
    /** False when the customer is frozen or not valid in the original store. */
    active: z.boolean(),
    priceList: z
      .object({
        number: z.number().int().min(2).max(10_000),
        name: text(100).nullable(),
        includesVat: z.boolean(),
      })
      .nullable(),
  }),
  items: z
    .array(
      z.object({
        itemCode: text(50),
        price: feedPriceSchema,
        special: z.boolean(),
        /**
         * Whether the original site shows the item to the customer (its category tree and stock).
         * The store shows its shoppers only these (adr/0046). Absent when the original store
         * cannot tell yet (just restarted, or from before adr/0046): the store keeps what it knew.
         */
        shown: z.boolean().optional(),
      }),
    )
    .max(50_000),
});
export type FeedCustomer = z.infer<typeof feedCustomerSchema>;
